import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Lock, Mail, Building2, Shield, Zap, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-md bg-blue-600 text-white grid place-items-center font-display font-bold text-lg">V</div>
            <div>
              <div className="font-display font-bold text-lg leading-none">Vijay Shankar Vyas</div>
              <div className="text-[10px] text-muted-foreground mt-1 tracking-widest uppercase">Chartered Accountants</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in to TPMS.</h1>
          <p className="text-sm text-muted-foreground mt-2">
            The operating system for your tax practice.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs font-medium">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-10"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-10"
                  required
                />
              </div>
            </div>

            {err && <div className="text-sm text-red-600 font-medium" data-testid="login-error">{err}</div>}

            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-10 bg-blue-600 hover:bg-blue-700">
              {busy ? "Signing in…" : "Sign in →"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: brand */}
      <div className="hidden lg:flex bg-slate-900 dark:bg-zinc-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 gridlines opacity-10" />
        <div className="relative m-auto max-w-md p-12">
          <div className="text-[10px] tracking-widest uppercase text-blue-400 mb-4">Practice Management · India</div>
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Every client. Every deadline.<br />
            <span className="text-blue-400">One control room.</span>
          </h2>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed">
            Replace Excel, WhatsApp follow-ups, and diaries. Manage GST, Income Tax, ROC, TDS &amp; more from a single secure platform.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Building2, title: "Client Master", desc: "PAN, GSTIN, TAN, CIN — unified profiles" },
              { icon: Shield, title: "Encrypted Vault", desc: "Portal logins secured with AES-256" },
              { icon: Zap, title: "Compliance Calendar", desc: "GSTR-1, 3B, ITR, TDS, ROC due dates" },
              { icon: TrendingUp, title: "Practice Dashboard", desc: "See workload, aging, revenue at a glance" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-blue-600/20 border border-blue-500/30 grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{title}</div>
                  <div className="text-xs text-slate-400">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
